import {
  AdminCreateInsuranceProduct,
  AdminDeleteInsuranceProduct,
  AdminFetchInsuranceProducts,
  AdminUpdateInsuranceProduct,
} from 'src/constants/actionTypes'
import type { InsuranceProduct } from 'src/interfaces/insuranceProduct'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { InsuranceProductState } from './types'

const getInitialState = (): InsuranceProductState => {
  return {
    insuranceProducts: [],
    insuranceProductsLoading: false,
    insuranceProductsError: null,
    saving: false,
    deletingId: null,
  }
}

export const insuranceProductReducer = (
  state: InsuranceProductState = getInitialState(),
  action: Action<unknown>,
): InsuranceProductState => {
  switch (action.type) {
    case AdminFetchInsuranceProducts.type.REQUEST:
      return {
        ...state,
        insuranceProductsLoading: true,
        insuranceProductsError: null,
      }
    case AdminFetchInsuranceProducts.type.SUCCESS: {
      const insuranceProducts = (action.payload as InsuranceProduct[] | undefined) ?? []

      return {
        ...state,
        insuranceProducts,
        insuranceProductsLoading: false,
        insuranceProductsError: null,
      }
    }
    case AdminFetchInsuranceProducts.type.FAILED:
      return {
        ...state,
        insuranceProductsLoading: false,
        insuranceProductsError: resolveErrorMessage(
          action.payload,
          'Failed to load insurance products',
        ),
      }

    // Create, update and delete never write to `insuranceProducts` — the list is refetched on
    // success, which is the single source of truth. See the design doc's §7.
    case AdminCreateInsuranceProduct.type.REQUEST:
    case AdminUpdateInsuranceProduct.type.REQUEST:
      return {
        ...state,
        saving: true,
      }
    case AdminCreateInsuranceProduct.type.SUCCESS:
    case AdminCreateInsuranceProduct.type.FAILED:
    case AdminUpdateInsuranceProduct.type.SUCCESS:
    case AdminUpdateInsuranceProduct.type.FAILED:
      return {
        ...state,
        saving: false,
      }

    case AdminDeleteInsuranceProduct.type.REQUEST: {
      const payload = action.payload as { insuranceProductId?: string } | undefined

      return {
        ...state,
        deletingId: payload?.insuranceProductId ?? null,
      }
    }
    // Cleared on FAILED as well as SUCCESS: a 403 in-use rejection must not leave the row's
    // Delete button spinning forever.
    case AdminDeleteInsuranceProduct.type.SUCCESS:
    case AdminDeleteInsuranceProduct.type.FAILED:
      return {
        ...state,
        deletingId: null,
      }

    default:
      return state
  }
}
